import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import ListPager from '../components/ListPager';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 50;

export default function ServiceCatalog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const fetchData = useCallback((opts) => {
    const params = {
      search: search || undefined,
      sort: sortField, order: sortDir,
      limit: LIMIT, offset: opts?.offset ?? offset,
    };
    axios.get(`${API}/admin/service-catalog`, { headers, params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); });
    // eslint-disable-next-line
  }, [search, sortField, sortDir, offset]);

  useEffect(() => {
    const t = setTimeout(() => fetchData(), 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggleSort = (field) => {
    setOffset(0);
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const arrow = (field) => (sortField !== field ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/admin/service-catalog`, { name: newName.trim() }, { headers });
      setNewName('');
      fetchData({ offset: 0 }); setOffset(0);
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось добавить'); }
    setBusy(false);
  };

  const toggleActive = async (item) => {
    await axios.patch(`${API}/admin/service-catalog/${item.id}`, { is_active: !item.is_active }, { headers });
    fetchData();
  };

  const startEdit = (item) => { setEditingId(item.id); setEditName(item.name); };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await axios.patch(`${API}/admin/service-catalog/${id}`, { name: editName.trim() }, { headers });
      setEditingId(null);
      fetchData();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось сохранить'); }
  };

  return (
    <Layout>
      <h1 style={styles.title}>Услуги клиник ({total})</h1>
      <div style={styles.hint}>
        Единый справочник медицинских услуг — клиники и сети выбирают из него в своём ЛК,
        что у них есть (не вводят название сами). Это унифицирует поиск врача по услуге при
        добавлении пациента. Пополняйте и актуализируйте список по мере развития платформы.
      </div>

      <div style={styles.addRow}>
        <input
          style={styles.addInput}
          placeholder="Название новой услуги"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && create()}
        />
        <button style={styles.btnPrimary} onClick={create} disabled={busy || !newName.trim()}>+ Добавить</button>
      </div>
      {err && <div style={styles.err}>{err}</div>}

      <div style={styles.table}>
        <div style={styles.thead}>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('name')}>
            Название{arrow('name')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('usage_count')}>
            Используют клиник{arrow('usage_count')}
          </div>
          <div style={styles.th}>Статус</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('created_at')}>
            Добавлено{arrow('created_at')}
          </div>
          <div style={styles.th}></div>
        </div>

        <div style={styles.filterRow}>
          <div style={styles.filterCell}>
            <input style={styles.filterInput} placeholder="Поиск по названию" value={search}
              onChange={e => { setSearch(e.target.value); setOffset(0); }} />
          </div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
        </div>

        {items.map(it => (
          <div key={it.id} style={styles.row}>
            <div style={styles.td}>
              {editingId === it.id ? (
                <input style={styles.editInput} value={editName} autoFocus
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveEdit(it.id)} />
              ) : it.name}
            </div>
            <div style={styles.td}>{it.usage_count}</div>
            <div style={styles.td}>
              <span style={it.is_active ? styles.badgeGreen : styles.badgeGray}>
                {it.is_active ? 'Активна' : 'Отключена'}
              </span>
            </div>
            <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>
              {new Date(it.created_at).toLocaleDateString('ru')}
            </div>
            <div style={{ ...styles.td, flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
              {editingId === it.id ? (
                <>
                  <span style={styles.link} onClick={() => saveEdit(it.id)}>Сохранить</span>
                  <span style={styles.linkGhost} onClick={() => setEditingId(null)}>Отмена</span>
                </>
              ) : (
                <>
                  <span style={styles.link} onClick={() => startEdit(it)}>Изменить</span>
                  <span style={styles.linkGhost} onClick={() => toggleActive(it)}>
                    {it.is_active ? 'Отключить' : 'Включить'}
                  </span>
                </>
              )}
            </div>
          </div>
        ))}

        {items.length === 0 && <div style={styles.empty}>Ничего не найдено</div>}
      </div>

      <ListPager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const styles = {
  title: { color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 12, marginTop: 0 },
  hint: { fontSize: 12, color: '#64748b', background: '#1e293b', borderRadius: 8, padding: '10px 16px', marginBottom: 20, borderLeft: '3px solid #334155', lineHeight: 1.5 },
  addRow: { display: 'flex', gap: 10, marginBottom: 16, maxWidth: 520 },
  addInput: { flex: 1, background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none' },
  btnPrimary: { background: '#2EC4B6', color: '#0f172a', border: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  err: { color: '#f87171', fontSize: 12, marginBottom: 12 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', gridTemplateColumns: '2fr 1.3fr 1fr 1fr 1.5fr', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  filterRow: { display: 'grid', gridTemplateColumns: '2fr 1.3fr 1fr 1fr 1.5fr', padding: '8px 20px', borderBottom: '1px solid #334155', background: '#172033', gap: 8 },
  filterCell: { display: 'flex', alignItems: 'center' },
  filterInput: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  row: { display: 'grid', gridTemplateColumns: '2fr 1.3fr 1fr 1fr 1.5fr', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0', alignItems: 'center' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  editInput: { background: '#0f172a', color: '#e2e8f0', border: '1px solid #2EC4B6', borderRadius: 6, padding: '5px 8px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '40px 0' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block', border: '1px solid #334155' },
  link: { color: '#2EC4B6', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  linkGhost: { color: '#64748b', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
};
